import { useState, useContext, useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { SessionContext } from '../../../Components/Context/SessionContext';
import { FeedbackContext } from "../../../Components/Context/FeedbackContext";
import { Loading } from "../../../Components/Loading/Loading";
import * as Api from '../../../api/Api';
import { useGetToken, useGetUser } from '../../../Hooks/CustomHooks';
import PermissionsGate from '../../../api/PermissionsGate';
import { SCOPES } from '../../../config/permissions-map';
import { Unauthorized } from '../../Global/unauthorized';
import * as Utilities from '../../../Components/Utils/Utils';
import { PageTitle } from '../../../../_metronic/layout/core';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import Modal from 'react-bootstrap/Modal';
import AgTable from '../../../Components/Table/AgTable';

registerLocale("es", es);


export default function VistaAmonestaciones({ title = "Vista de Amonestaciones" }) {

    const { session } = useContext(SessionContext);
    const user = useGetUser();
    const currentRoleId = String(user?.role?.id || session?.role || '');
    const roleName = String(user?.role?.name || '').toLowerCase();

    const isComercial = currentRoleId === '97' || currentRoleId === '111' || roleName === 'comercial' || roleName.includes('comercial');
    const isCobranzas = currentRoleId === '120' || currentRoleId === '108' || roleName === 'cobranzas' || roleName.includes('cobranza');
    const isSubgerencia = currentRoleId === '118' || currentRoleId === '20' || currentRoleId === '1' || roleName.includes('subgeren');
    const isOperaciones = currentRoleId === '119' || currentRoleId === '2' || currentRoleId === '104' || roleName.includes('operacion');
    const isReviewerOnly = (isComercial || isCobranzas) && !isSubgerencia && !isOperaciones;
    const canApprove = (isSubgerencia || isOperaciones) && !isReviewerOnly;

    const [registrar, setRegistrar] = useState(false);
    const [apruebaDesaprueba, setApruebaDesaprueba] = useState(true);
    const [currentEditSubgerenciaObservacion, setCurrentEditSubgerenciaObservacion] = useState('');
    const [currentEditOperacionesObservacion, setCurrentEditOperacionesObservacion] = useState('');
    const [currentEditComercialObservacion, setCurrentEditComercialObservacion] = useState('');
    const [currentEditCobranzaObservacion, setCurrentEditCobranzaObservacion] = useState('');
    const [descuentoPorcentajeEdit, setDescuentoPorcentajeEdit] = useState(0);

    const getObservadorRole = () => {
        if (isComercial) return currentRoleId === '97' ? '97' : '111';
        if (isCobranzas) return currentRoleId === '120' ? '120' : '108';
        if (isSubgerencia) return '118';
        if (isOperaciones) return '119';
        return currentRoleId || '118';
    };

    const getObservacionAEnviar = () => {
        if (isComercial) return currentEditComercialObservacion;
        if (isCobranzas) return currentEditCobranzaObservacion;
        if (isSubgerencia) return currentEditSubgerenciaObservacion;
        if (isOperaciones) return currentEditOperacionesObservacion;
        return '';
    };

    const [verDetalle, setVerDetalle] = useState(false);
    const [currentVistaAmonestacion, setCurrentVistaAmonestacion] = useState({
        "idAmonestacion": "",
        "tipoMotivo": "",
        "motivo": "",
        "locatario": "",
        "numeroContrato": "",
        "montoBase": 0,
        "descuentoPorcentaje": 0,
        "montoFinal": 0,
        "fecha": "",
        "aprobacion_subgerencia": null,
        "aprobacion_operaciones": null,
        "observacion_subgerencia": "",
        "observacion_operaciones": "",
        "comentario_comercial": "",
        "comentario_cobranzas": "",
        "encargado": "",
        "dni": '',
        "detalle": "",
        "imagenUrl": ""
    });

    const { feedbackContext, setFeedbackContext } = useContext(FeedbackContext);
    const token = useGetToken();
    const [allData, setAllData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(new Date());

    const { data: apiResponse, isLoading: isLoadingTable, refetch } = useQuery(
        ["VistaAmonestacionGeneral", { token }],
        async () => await Api.VistaAmonestacion({ token }),
        { enabled: Boolean(token), refetchOnWindowFocus: false }
    );

    useEffect(() => {
        if (apiResponse && apiResponse.data) {
            const table = apiResponse.data.table || apiResponse.data || [];
            if (Array.isArray(table)) {
                setAllData(table);
            }
        }
    }, [apiResponse]);

    const filteredData = useMemo(() => {
        if (!allData || !Array.isArray(allData)) return [];
        if (!selectedMonth) return allData;

        const targetMonth = selectedMonth.getMonth() + 1;
        const targetYear = selectedMonth.getFullYear();

        return allData.filter(item => {
            const dateStr = item.fechaInicio || item.fecha || '';
            if (!dateStr) return false;

            let m = null;
            let y = null;

            if (dateStr.includes('/')) {
                const parts = dateStr.split(' ')[0].split('/');
                if (parts.length === 3) {
                    m = parseInt(parts[1], 10);
                    y = parseInt(parts[2], 10);
                }
            } else if (dateStr.includes('-')) {
                const parts = dateStr.split('T')[0].split(' ')[0].split('-');
                if (parts.length === 3) {
                    y = parseInt(parts[0], 10);
                    m = parseInt(parts[1], 10);
                }
            }

            return m === targetMonth && y === targetYear;
        });
    }, [allData, selectedMonth]);

    const [guardadoExitoso, setGuardadoExitoso] = useState(false);

    function guardadoExistosoModal() {
        window.location.reload(false);
    }

    let header = [
        {
            headerName: 'Tipo Motivo',
            field: 'tipoMotivo',
        },
        {
            headerName: 'Motivo',
            field: 'motivo',
        },
        {
            headerName: 'Locatario',
            field: 'locatario',
        },
        {
            headerName: 'Número de contrato',
            field: 'numeroContrato',
            valueGetter: (params) => params.data?.numeroContrato || params.data?.nroContrato || params.data?.contrato || params.data?.codigoContrato || '',
        },
        {
            headerName: 'Monto Final',
            field: 'montoFinal',
            type: 'rightAligned',
            cellRenderer: (props) => (
                <>{Utilities.comillasFix(props.data?.montoFinal, true)}</>
            )
        },
        {
            headerName: 'Fecha',
            field: 'fecha',
            comparator: Utilities.dateComparator,
        },
        {
            headerName: 'Comentarios',
            className: 'text-center',
            children: [
                {
                    headerName: 'Comercial',
                    field: 'comentario_comercial',
                    valueGetter: (params) => params.data?.comentario_comercial ?? params.data?.comentarioComercial ?? params.data?.observacion_comercial ?? params.data?.observacionComercial ?? '',
                },
                {
                    headerName: 'Cobranzas',
                    field: 'comentario_cobranzas',
                    valueGetter: (params) => params.data?.comentario_cobranzas ?? params.data?.comentarioCobranzas ?? params.data?.observacion_cobranzas ?? params.data?.observacionCobranzas ?? '',
                }
            ]
        },
        {
            headerName: 'Aprobaciones',
            className: 'text-center',
            children: [
                {
                    headerName: 'Subgerencia mall',
                    field: 'aprobacion_subgerencia',
                    type: 'rightAligned',
                    valueGetter: (params) => {
                        const val = params.data?.aprobacion_subgerencia;
                        if (val === true || val === 1 || val === 'true') {
                            return 'Aprobado';
                        }
                        if (val === false || val === 0 || val === 'false') {
                            return 'Desaprobado';
                        }
                        return 'Pendiente';
                    },
                    cellRenderer: (props) => {
                        const val = props.value || (props.data?.aprobacion_subgerencia === true || props.data?.aprobacion_subgerencia === 1 || props.data?.aprobacion_subgerencia === 'true' ? 'Aprobado' : (props.data?.aprobacion_subgerencia === false || props.data?.aprobacion_subgerencia === 0 || props.data?.aprobacion_subgerencia === 'false' ? 'Desaprobado' : 'Pendiente'));
                        if (val === 'Aprobado') {
                            return <span className="badge badge-light-success">Aprobado</span>;
                        }
                        if (val === 'Desaprobado') {
                            return <span className="badge badge-light-danger">Desaprobado</span>;
                        }
                        return <span className="badge badge-light-warning">Pendiente</span>;
                    }
                },
                {
                    headerName: 'Operaciones',
                    field: 'aprobacion_operaciones',
                    type: 'rightAligned',
                    valueGetter: (params) => {
                        const val = params.data?.aprobacion_operaciones;
                        if (val === true || val === 1 || val === 'true') {
                            return 'Aprobado';
                        }
                        if (val === false || val === 0 || val === 'false') {
                            return 'Desaprobado';
                        }
                        return 'Pendiente';
                    },
                    cellRenderer: (props) => {
                        const val = props.value || (props.data?.aprobacion_operaciones === true || props.data?.aprobacion_operaciones === 1 || props.data?.aprobacion_operaciones === 'true' ? 'Aprobado' : (props.data?.aprobacion_operaciones === false || props.data?.aprobacion_operaciones === 0 || props.data?.aprobacion_operaciones === 'false' ? 'Desaprobado' : 'Pendiente'));
                        if (val === 'Aprobado') {
                            return <span className="badge badge-light-success">Aprobado</span>;
                        }
                        if (val === 'Desaprobado') {
                            return <span className="badge badge-light-danger">Desaprobado</span>;
                        }
                        return <span className="badge badge-light-warning">Pendiente</span>;
                    }
                },
                {
                    headerName: 'Estado General',
                    field: 'estado_general',
                    type: 'rightAligned',
                    valueGetter: (params) => {
                        const op = params.data?.aprobacion_operaciones;
                        const sub = params.data?.aprobacion_subgerencia;
                        if (op === true || op === 1 || op === 'true') {
                            return 'Aprobado';
                        }
                        if (op === false || op === 0 || op === 'false' || sub === false || sub === 0 || sub === 'false') {
                            return 'Desaprobado';
                        }
                        return 'Pendiente';
                    },
                    cellRenderer: (props) => {
                        const val = props.value || ((props.data?.aprobacion_operaciones === true || props.data?.aprobacion_operaciones === 1 || props.data?.aprobacion_operaciones === 'true') ? 'Aprobado' : ((props.data?.aprobacion_operaciones === false || props.data?.aprobacion_operaciones === 0 || props.data?.aprobacion_operaciones === 'false' || props.data?.aprobacion_subgerencia === false || props.data?.aprobacion_subgerencia === 0 || props.data?.aprobacion_subgerencia === 'false') ? 'Desaprobado' : 'Pendiente'));
                        if (val === 'Aprobado') {
                            return <span className="badge badge-success">Aprobado</span>;
                        }
                        if (val === 'Desaprobado') {
                            return <span className="badge badge-light-danger">Desaprobado</span>;
                        }
                        return <span className="badge badge-light-warning">Pendiente</span>;
                    }
                }
            ]
        },
        {
            headerName: 'DETALLE',
            className: 'min-w-50px text-center',
            cellRenderer: (props) => (
                <button className="btn btn-light btn-active-light-primary btn-sm"
                    onClick={() => {
                        setCurrentVistaAmonestacion(props.data);
                        setCurrentEditSubgerenciaObservacion(props.data?.observacion_subgerencia || '');
                        setCurrentEditOperacionesObservacion(props.data?.observacion_operaciones || '');
                        setCurrentEditComercialObservacion(props.data?.comentario_comercial || props.data?.observacion_comercial || '');
                        setCurrentEditCobranzaObservacion(props.data?.comentario_cobranzas || props.data?.observacion_cobranzas || '');
                        setDescuentoPorcentajeEdit(props.data?.descuentoPorcentaje || 0);
                        setVerDetalle(true);
                    }}
                >
                    <i className="fi-rr-interactive"></i>
                </button >
            )
        }
    ];

    let { data } = useQuery(
        ["AprobarDesaprobarAmonestacion", {
            idAmonestacion: currentVistaAmonestacion.idAmonestacion,
            apruebaDesaprueba: apruebaDesaprueba,
            observacionCurrentEdit: getObservacionAEnviar(),
            observadorRole: getObservadorRole(),
            descuentoPorcentaje: canApprove ? descuentoPorcentajeEdit : 0,
            token: token
        }], async () => await Api.RegistrarAprobacionDesaprobacionAmonestacion({
            idAmonestacion: currentVistaAmonestacion.idAmonestacion,
            apruebaDesaprueba: apruebaDesaprueba,
            observacionCurrentEdit: getObservacionAEnviar(),
            observadorRole: getObservadorRole(),
            descuentoPorcentaje: canApprove ? descuentoPorcentajeEdit : 0,
            token: token
        }),
        { enabled: Boolean(registrar) }
    );
    useEffect(() => {
        if (data) {
            if (!data.success)
                setFeedbackContext({ ...feedbackContext, showError: true, idError: data.errorId });
            else {
                setVerDetalle(false);
                setRegistrar(false);
                setGuardadoExitoso(true);
            }
        }
    }, [data]);

    return (
        <>
            <PermissionsGate RenderError={() => <Unauthorized />} scopes={[SCOPES.VISTA_AMONESTACIONES]} >
                <PageTitle>{title}</PageTitle>
                <div className="card my-5">
                    <div className="card-header border-0 pt-6 row g-3 flex-wrap d-flex flex-row" style={{ zIndex: 10 }}>
                        <div className="col-12 col-sm-4 col-md-3">
                            <div className="fw-bold fs-7 text-gray-600 mb-1">Mes:</div>
                            <DatePicker
                                className="form-control form-control-transparent pe-5"
                                locale="es"
                                selected={selectedMonth}
                                onChange={(dateX) => setSelectedMonth(dateX)}
                                dateFormat="MM/yyyy"
                                showMonthYearPicker
                                isClearable={true}
                                placeholderText="Seleccione mes..."
                            />
                        </div>
                    </div>

                    <div className="card-body pt-0">
                        <AgTable
                            idContent={"Vista Amonestacion"}
                            localData={filteredData}
                            setAuxData={null}
                            header={header}
                            titleExcel={"Vista Amonestacion "}
                            conditions={true}
                            hideFilter={false}
                            paginationPageSize={15}
                            rowHeight={40}
                            useFlex={false}
                        />
                    </div>
                </div>

                <Modal size="lg" show={verDetalle} onHide={() => setVerDetalle(false)} className=''>
                    <Modal.Header className='modal-header'>
                        <Modal.Title className='fw-bolder d-flex justify-content-end'>
                            <h3>Detalle de Amonestación</h3>
                            {Boolean(registrar) && <Loading></Loading>}
                        </Modal.Title>
                        <button type="button" onClick={() => setVerDetalle(false)} className="btn btn-icon btn-sm btn-active-icon-primary">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black"></rect>
                                <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black"></rect>
                            </svg>
                        </button>
                    </Modal.Header>
                    <Modal.Body className=''>
                        <div className='row'>
                            <div className=' col-md-4 card card-flush pt-3 mb-0'>
                                <div className='card-body pt-2'>
                                    <div className="fw-bolder text-gray-800 fs-6 mb-1">Motivo</div>
                                    <div className="text-gray-700 fs-6">{currentVistaAmonestacion.motivo || '-'}</div>
                                    <div className="separator separator-dashed mb-5"></div>

                                    <div className="fw-bolder text-gray-800 fs-6 mb-1">Detalle</div>
                                    <div className="text-gray-700 fs-6" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {currentVistaAmonestacion.detalle || '-'}
                                    </div>
                                    <div className="separator separator-dashed mb-5"></div>

                                    <div className="fw-bolder text-gray-800 fs-6 mb-1">Locatario</div>
                                    <div className="text-gray-700 fs-6">{currentVistaAmonestacion.locatario || '-'}</div>
                                    <div className="separator separator-dashed mb-5"></div>

                                    {(currentVistaAmonestacion.numeroContrato || currentVistaAmonestacion.nroContrato || currentVistaAmonestacion.contrato || currentVistaAmonestacion.codigoContrato) && (
                                        <>
                                            <div className="fw-bolder text-gray-800 fs-6 mb-1">Número de contrato</div>
                                            <div className="text-gray-700 fs-6">{currentVistaAmonestacion.numeroContrato || currentVistaAmonestacion.nroContrato || currentVistaAmonestacion.contrato || currentVistaAmonestacion.codigoContrato}</div>
                                            <div className="separator separator-dashed mb-5"></div>
                                        </>
                                    )}

                                    <div className="fw-bolder text-gray-800 fs-6 mb-1">Encargado</div>
                                    <div className="text-gray-700 fs-6">{currentVistaAmonestacion.encargado || '-'}</div>
                                    <div className="separator separator-dashed mb-5"></div>

                                    {currentVistaAmonestacion.montoBase > 0 && (
                                        <>
                                            <div className="fw-bolder text-gray-800 fs-6 mb-1">Monto Base</div>
                                            <div className="text-gray-700 fs-6">S/. {Utilities.comillasFix(currentVistaAmonestacion.montoBase, true)}</div>
                                            <div className="separator separator-dashed mb-5"></div>
                                        </>
                                    )}

                                    <div className="fw-bolder text-gray-800 fs-6 mb-1">Porcentaje de descuento</div>
                                    <div className="d-flex align-items-center mt-1">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="100"
                                            disabled={!canApprove}
                                            readOnly={!canApprove}
                                            className="form-control form-control-solid fs-6 me-2"
                                            style={{ maxWidth: "120px" }}
                                            value={descuentoPorcentajeEdit}
                                            onChange={(e) => setDescuentoPorcentajeEdit(e.target.value)}
                                        />
                                        <span className="text-gray-700 fs-6">%</span>
                                    </div>

                                    {currentVistaAmonestacion.montoFinal > 0 && (
                                        <>
                                            <div className="separator separator-dashed my-5"></div>
                                            <div className="fw-bolder text-gray-800 fs-6 mb-1">Monto Final</div>
                                            <div className="text-gray-700 fs-6">S/. {Utilities.comillasFix(currentVistaAmonestacion.montoFinal, true)}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className=' col-md-8 card card-flush pt-3 mb-0'>
                                {currentVistaAmonestacion.imagenUrl && currentVistaAmonestacion.imagenUrl.toLowerCase().includes('.pdf') ? (
                                    <iframe src={currentVistaAmonestacion.imagenUrl} title="Documento PDF" className="w-100 card-rounded min-h-350px" style={{ border: 'none', minHeight: '350px' }} />
                                ) : (
                                    <img src={currentVistaAmonestacion.imagenUrl} className="card-rounded min-h-350px" alt="Documento" />
                                )}
                            </div>
                        </div>
                        <div className='card card-flush pt-3 mb-0'>
                            <div className="card-header">
                                <div className="card-title">
                                    <h3 className="fw-bolder">Observaciones y Comentarios</h3>
                                </div>
                            </div>

                            <div className='card-body pt-0 fs-6'>
                                <div className='mb-5'>
                                    <div className='fs-6 fw-bolder form-label mb-2'>Subgerencia mall</div>
                                    <textarea
                                        className='form-control form-control-solid rounded-3'
                                        type='text'
                                        placeholder='Observación subgerencia...'
                                        value={currentEditSubgerenciaObservacion}
                                        readOnly={!isSubgerencia}
                                        onChange={(e) => {
                                            setCurrentEditSubgerenciaObservacion(e.target.value);
                                        }}
                                    ></textarea>
                                    {isSubgerencia && currentEditSubgerenciaObservacion.replaceAll(/\s/g, '').length === 0 && <span className="text-danger">Ingresar alguna observación</span>}
                                </div>

                                <div className='mb-5'>
                                    <div className='fs-6 fw-bolder form-label mb-2'>Operaciones</div>
                                    <textarea
                                        className='form-control form-control-solid rounded-3'
                                        type='text'
                                        placeholder='Observación operaciones...'
                                        value={currentEditOperacionesObservacion}
                                        readOnly={!isOperaciones}
                                        onChange={(e) => {
                                            setCurrentEditOperacionesObservacion(e.target.value);
                                        }}
                                    ></textarea>
                                    {isOperaciones && currentEditOperacionesObservacion.replaceAll(/\s/g, '').length === 0 && <span className="text-danger">Ingresar alguna observación</span>}
                                </div>

                                <div className='mb-5'>
                                    <div className='fs-6 fw-bolder form-label mb-2'>Comercial</div>
                                    <textarea
                                        className='form-control form-control-solid rounded-3'
                                        type='text'
                                        placeholder='Comentario comercial...'
                                        value={currentEditComercialObservacion}
                                        readOnly={!isComercial}
                                        onChange={(e) => {
                                            setCurrentEditComercialObservacion(e.target.value);
                                        }}
                                    ></textarea>
                                    {isComercial && currentEditComercialObservacion.replaceAll(/\s/g, '').length === 0 && <span className="text-danger">Ingresar algún comentario</span>}
                                </div>

                                <div className='mb-5'>
                                    <div className='fs-6 fw-bolder form-label mb-2'>Cobranzas</div>
                                    <textarea
                                        className='form-control form-control-solid rounded-3'
                                        type='text'
                                        placeholder='Comentario cobranza...'
                                        value={currentEditCobranzaObservacion}
                                        readOnly={!isCobranzas}
                                        onChange={(e) => {
                                            setCurrentEditCobranzaObservacion(e.target.value);
                                        }}
                                    ></textarea>
                                    {isCobranzas && currentEditCobranzaObservacion.replaceAll(/\s/g, '').length === 0 && <span className="text-danger">Ingresar algún comentario</span>}
                                </div>
                            </div>

                        </div>

                    </Modal.Body >
                    <Modal.Footer className='text-center'>
                        {canApprove && (
                            <>
                                <button className="btn btn-light me-3" onClick={() => {
                                    setApruebaDesaprueba(false);
                                    setRegistrar(true);
                                }}>
                                    Desaprobar
                                </button>
                                <button className="btn btn-primary" onClick={() => {
                                    setApruebaDesaprueba(true);
                                    setRegistrar(true);
                                }}>
                                    Aprobar
                                </button>
                            </>
                        )}
                        {isReviewerOnly && (
                            <button
                                className="btn btn-primary"
                                disabled={
                                    (isComercial && currentEditComercialObservacion.replaceAll(/\s/g, '').length === 0) ||
                                    (isCobranzas && currentEditCobranzaObservacion.replaceAll(/\s/g, '').length === 0)
                                }
                                onClick={() => {
                                    setApruebaDesaprueba(true);
                                    setRegistrar(true);
                                }}
                            >
                                <i className="fi-rr-disk me-1"></i> Guardar Comentario
                            </button>
                        )}
                        {!canApprove && !isReviewerOnly && (
                            <button className="btn btn-light" onClick={() => setVerDetalle(false)}>
                                Cerrar
                            </button>
                        )}
                    </Modal.Footer>
                </Modal >

                <Modal
                    size="sm"
                    show={guardadoExitoso}
                    onHide={() => setGuardadoExitoso(false)}
                    aria-labelledby="contained-modal-title-vcenter"
                    centered
                >
                    <Modal.Header>
                        <Modal.Title id="example-modal-sizes-title-sm" style={{ margin: 'auto' }}>
                            Guardado Exitoso
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Footer style={{ margin: 'auto' }}>
                        <button type="button" className="btn btn-primary" onClick={guardadoExistosoModal}>
                            Aceptar
                        </button>
                    </Modal.Footer>
                </Modal>
            </PermissionsGate >
        </>
    )
}
